import { ChevronDown } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/locales/client";

interface OrderQuestionDropdownProps {
  isAuthenticated: boolean;
}

type OrderOption = "createdAt" | "progress" | "alphabetical";

interface OrderOptionsType {
  [key: string]: string;
}

const OrderQuestionDropdown = ({
  isAuthenticated,
}: OrderQuestionDropdownProps) => {
  const t = useI18n();
  const getOrderOptions = (isAuthenticated: boolean): OrderOptionsType => {
    const baseOptions = {
      createdAt: t("orderByOptions.original"),
      alphabetical: t("orderByOptions.alphabetical"),
    };

    return isAuthenticated
      ? { ...baseOptions, progress: t("orderByOptions.studyProgress") }
      : baseOptions;
  };
  const [orderBy, setOrderBy] = useQueryState("orderBy", parseAsString);
  const ORDER_OPTIONS = getOrderOptions(isAuthenticated);

  const handleSelect = (value: OrderOption) => {
    setOrderBy(value);
  };

  // If the current orderBy is 'progress' and user is not authenticated,
  // or if orderBy is invalid/null, default to "Created At"
  const currentLabel =
    (!isAuthenticated && orderBy === "progress") ||
    !orderBy ||
    !ORDER_OPTIONS[orderBy]
      ? ORDER_OPTIONS.createdAt
      : ORDER_OPTIONS[orderBy];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6">
          {currentLabel}{" "}
          <ChevronDown className="text-muted-foreground size-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("orderBy")}</DropdownMenuLabel>
        {Object.entries(ORDER_OPTIONS).map(([value, label]) => (
          <DropdownMenuItem
            key={value}
            onClick={() => handleSelect(value as OrderOption)}
            className="cursor-pointer"
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrderQuestionDropdown;
